/**
 * US-47: вкладка «Выгрузка 1С ЗУП».
 *
 * - Селектор месяца (дефолт — предыдущий месяц).
 * - Кнопка «Обновить предпросмотр» — preview API.
 * - Сводка: сколько строк, период, организация, кто формирует.
 * - Список warnings (если есть проекты без zup-реквизитов или
 *   незавершённые недели).
 * - Кнопка «Скачать xlsx».
 *
 * Доступ контролируется в App.tsx (вкладка отображается только для HR/PMO/LR/admin).
 */

import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Group,
  List,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { AlertCircle, Download, FilePlus, RefreshCcw } from "lucide-react";
import {
  downloadZupReportXlsx,
  fetchZupFillPlan,
  fetchZupReportPreview,
  type ZupFillPlan,
  type ZupReportPreview,
} from "../api/client";
import { formatHours } from "../utils/format";

function previousMonthString(today: Date = new Date()): string {
  // YYYY-MM для месяца, предшествующего `today`.
  const d = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function monthOptions(today: Date = new Date()): { value: string; label: string }[] {
  // Последние 12 месяцев, начиная с предыдущего.
  const months: { value: string; label: string }[] = [];
  const RU_MONTHS = [
    "Январь",
    "Февраль",
    "Март",
    "Апрель",
    "Май",
    "Июнь",
    "Июль",
    "Август",
    "Сентябрь",
    "Октябрь",
    "Ноябрь",
    "Декабрь",
  ];
  for (let i = 1; i <= 12; i += 1) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    months.push({
      value: `${y}-${m}`,
      label: `${RU_MONTHS[d.getMonth()]} ${y}`,
    });
  }
  return months;
}

function formatPeriod(iso: string): string {
  // YYYY-MM-DD → DD.MM.YYYY.
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
}

export function ZupExportView({ canFill = false }: { canFill?: boolean }) {
  const [month, setMonth] = useState<string>(() => previousMonthString());
  const [data, setData] = useState<ZupReportPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // US-112: план дозаполнения. Запрашивается только по нажатию и только чтобы
  // показать состав — скачивание идёт отдельным действием, после подтверждения.
  const [plan, setPlan] = useState<ZupFillPlan | null>(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);

  const openFillPlan = () => {
    setPlanLoading(true);
    setPlanError(null);
    fetchZupFillPlan(month)
      .then(setPlan)
      .catch((err: Error) => setPlanError(err.message))
      .finally(() => setPlanLoading(false));
  };

  const fetchData = (m: string) => {
    setLoading(true);
    setError(null);
    fetchZupReportPreview(m)
      .then((next) => setData(next))
      .catch((err: Error) => {
        setError(err.message);
        setData(null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData(month);
  }, [month]);

  const options = monthOptions();

  return (
    <Stack gap="md" data-testid="zup-export-view">
      <Group justify="space-between" align="end">
        <Stack gap={4}>
          <Title order={3}>Выгрузка трудозатрат в 1С ЗУП</Title>
          <Text c="dimmed" size="sm">
            Выберите месяц. В файл попадают только согласованные часы. Сверхурочные — всё что свыше
            нормы дня плюс выходные/праздники.
          </Text>
        </Stack>
        <Group gap="xs">
          <Select
            label="Период"
            data={options}
            value={month}
            onChange={(v) => v && setMonth(v)}
            data-testid="zup-month-select"
            w={220}
          />
          <Button
            variant="default"
            leftSection={<RefreshCcw size={14} />}
            onClick={() => fetchData(month)}
            loading={loading}
            disabled={loading}
          >
            Обновить
          </Button>
        </Group>
      </Group>

      {error && (
        <Alert color="red" icon={<AlertCircle size={16} />} title="Ошибка">
          {error}
        </Alert>
      )}

      {loading && !data && (
        <Group justify="center" py="xl">
          <Loader size="sm" />
          <Text c="dimmed">Считаем трудозатраты…</Text>
        </Group>
      )}

      {data && !loading && (
        <>
          <Paper p="md" withBorder data-testid="zup-summary">
            <Group gap="xl" wrap="wrap">
              <Stack gap={2}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Период
                </Text>
                <Text fw={500}>{formatPeriod(data.period_date)}</Text>
              </Stack>
              <Stack gap={2}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Организация
                </Text>
                <Text fw={500}>{data.organization}</Text>
              </Stack>
              <Stack gap={2}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Пользователь
                </Text>
                <Text fw={500}>{data.user_full_name || "—"}</Text>
              </Stack>
              <Stack gap={2}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Дата записи
                </Text>
                <Text fw={500}>{formatPeriod(data.record_date)}</Text>
              </Stack>
              <Stack gap={2}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Строк в файле
                </Text>
                <Text fw={500} data-testid="zup-rows-count">
                  {data.rows_count}
                </Text>
              </Stack>
            </Group>
          </Paper>

          {data.warnings.length > 0 && (
            <Alert
              color="yellow"
              icon={<AlertCircle size={16} />}
              title="Внимание"
              data-testid="zup-warnings"
            >
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {data.warnings.map((w, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>
                    <Text size="sm">{w}</Text>
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          <Group justify="flex-end">
            {canFill && (
              <Button
                variant="default"
                leftSection={<FilePlus size={14} />}
                onClick={openFillPlan}
                loading={planLoading}
                data-testid="zup-fill-btn"
              >
                Дозаполнить…
              </Button>
            )}
            <Button
              leftSection={<Download size={14} />}
              onClick={() => downloadZupReportXlsx(month)}
              disabled={data.rows_count === 0}
              data-testid="zup-download-btn"
            >
              Скачать xlsx
            </Button>
          </Group>

          {planError && (
            <Alert color="red" icon={<AlertCircle size={16} />} title="Не удалось построить план">
              {planError}
            </Alert>
          )}

          {data.rows_count > 0 ? (
            <Paper withBorder>
              <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Сотрудник</Table.Th>
                    <Table.Th>Проект</Table.Th>
                    <Table.Th>Вид часов</Table.Th>
                    <Table.Th ta="right">ЧЧ</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.preview_rows.map((r, i) => (
                    <Table.Tr key={i}>
                      <Table.Td>{r.employee}</Table.Td>
                      <Table.Td>{r.project}</Table.Td>
                      <Table.Td>{r.kind}</Table.Td>
                      <Table.Td ta="right">{formatHours(r.hours)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              {data.rows_count > data.preview_rows.length && (
                <Text p="sm" c="dimmed" size="sm">
                  Показаны первые {data.preview_rows.length} из {data.rows_count} строк. Полный файл
                  — кнопка «Скачать xlsx».
                </Text>
              )}
            </Paper>
          ) : (
            <Paper p="md" withBorder>
              <Text c="dimmed">
                За выбранный период нет утверждённых строк с заполненными реквизитами проектов в 1С.
                Проверьте предупреждения выше.
              </Text>
            </Paper>
          )}
        </>
      )}

      {/* US-112. Список пофамильный и НЕ сокращается: в файле для 1С колонки
          «Источник» нет, и после выгрузки эти часы уже не отличить от
          настоящих — значит решение должно приниматься по именам, а не по
          одному итоговому числу. */}
      <Modal
        opened={plan !== null}
        onClose={() => setPlan(null)}
        title="Подтвердите дозаполнение выгрузки"
        size="lg"
      >
        {plan && (
          <Stack gap="sm">
            {plan.blocker ? (
              <Alert color="yellow" variant="light" icon={<AlertCircle size={16} />}>
                Дозаполнить нечем: {plan.blocker}.
              </Alert>
            ) : plan.total_employees === 0 && plan.skipped.length > 0 ? (
              /* ⚠️ Состояние, которое легко перепутать с «всё закрыто», а оно
                 ему противоположно: недостача есть у всех в списке ниже,
                 просто дозаполнить их нечем. Сегодня на проде это рядовой
                 случай — правила распределения заведены не у всех. Сказать
                 здесь «норма закрыта» значит соврать ровно в том месте, ради
                 честности которого вся эта кнопка и сделана. */
              <Text>
                Ни одного сотрудника дозаполнить не удалось: у всех, у кого есть недостача, не
                хватает данных. Причины — ниже.
              </Text>
            ) : plan.total_employees === 0 ? (
              <Text>
                Дозаполнять нечего: у всех, кто ведёт таймшит, норма месяца закрыта согласованными
                часами или отсутствиями.
              </Text>
            ) : (
              <>
                <Text>
                  Выгрузка будет дополнена расчётными часами:{" "}
                  <b>
                    {plan.total_employees} сотрудников, {formatHours(plan.total_hours)} ч
                  </b>
                  .
                </Text>

                <ScrollArea.Autosize mah={320}>
                  <Table striped withTableBorder data-testid="zup-fill-table">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Сотрудник</Table.Th>
                        <Table.Th>Куда списываем</Table.Th>
                        <Table.Th style={{ textAlign: "right" }}>Часы</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {plan.entries.flatMap((e) =>
                        e.targets.map((t) => (
                          <Table.Tr key={`${e.employee}-${t.direction}`}>
                            <Table.Td>{e.employee}</Table.Td>
                            <Table.Td>
                              {t.direction} → {t.project_code}
                            </Table.Td>
                            <Table.Td style={{ textAlign: "right" }}>
                              {formatHours(t.hours)}
                            </Table.Td>
                          </Table.Tr>
                        )),
                      )}
                    </Table.Tbody>
                  </Table>
                </ScrollArea.Autosize>

                <Text size="sm">
                  Дополненные часы не были введены сотрудниками и не утверждены руководителями
                  проектов. После загрузки в 1С они станут основанием для расчёта.
                </Text>
              </>
            )}

            {plan.skipped.length > 0 && (
              <Alert
                color="yellow"
                variant="light"
                title="Этих сотрудников дозаполнить не удалось"
                icon={<AlertCircle size={16} />}
              >
                <List size="sm" spacing={4}>
                  {plan.skipped.map((sk) => (
                    <List.Item key={sk.employee}>
                      {sk.employee} — {formatHours(sk.hours)} ч: {sk.reason}
                    </List.Item>
                  ))}
                </List>
              </Alert>
            )}

            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={() => setPlan(null)}>
                Отмена
              </Button>
              <Button
                leftSection={<Download size={14} />}
                disabled={plan.total_employees === 0 || plan.blocker !== null}
                onClick={() => {
                  downloadZupReportXlsx(month, true);
                  setPlan(null);
                }}
                data-testid="zup-fill-confirm-btn"
              >
                Дозаполнить и скачать
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
